<template>
    <MyWorkInsightCard
        :experiment-title="category"
        :experiment-slug="experimentSlug"
        :title="title"
        :summary="summary"
        :insight-id="insightId"
        :evidence-groups="evidenceGroups"
        location="mywork_homepage_carousel"
        :show-close="showClose"
        @close="emit('close')">
        <template #feedback>
            <MyWorkInsightFeedback
                variant="buttons"
                :feedback-value="feedbackValue"
                :feedback-tags="feedbackTags"
                :feedback-comment="feedbackComment"
                :feedback-submitting="feedbackSubmitting || Boolean(feedActions?.isPending(insightId))"
                :submit-error="!!submitError"
                reason-prompt="never"
                :dive-deeper-loading="isStartingChat"
                @select="handleFeedbackSelect"
                @dive-deeper="emit('diveDeeper')" />
            <span v-if="chatError" role="alert" class="details mt-1 text-chip-status-overdue-text text-pretty">{{ chatError }}</span>
        </template>
    </MyWorkInsightCard>
</template>

<script setup lang="ts">
import MyWorkInsightCard from "~vue/components/MyWorkInsightCard.vue";
import { useLabInsightActions } from "~vue/composables/useLabInsightActions";
import { insightFeedActions } from "~vue/composables/useMyWorkInsightFeed";
import { evidenceSourceCounts, type InsightEvidenceGroup } from "~vue/Labs/evidence";
import MyWorkInsightFeedback from "~vue/Labs/MyWorkInsightFeedback.vue";
import type { LabInsightFeedbackResponse } from "~vue/types/labs";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { inject, onMounted } from "vue";

const props = withDefaults(
    defineProps<{
        insightId: string;
        category: string;
        experimentSlug: string;
        title: string;
        summary: string;
        showClose?: boolean;
        evidenceGroups?: InsightEvidenceGroup[];
        /** Server-rendered existing feedback (null when there is none), so the card renders its final state on first paint. */
        initialFeedbackValue?: number | null;
        /** Owned by the carousel (HomeMyWorkInsightCarousel.vue), which shares one useLabInsightChat
         * call across the currently-shown card rather than one per card instance. */
        isStartingChat?: boolean;
        chatError?: string | null;
    }>(),
    {
        showClose: false,
        initialFeedbackValue: null,
        isStartingChat: false,
        chatError: null,
    },
);

const emit = defineEmits<{ close: []; diveDeeper: []; feedback: [response?: LabInsightFeedbackResponse] }>();

const {
    feedbackValue,
    feedbackTags,
    feedbackComment,
    feedbackSubmitting,
    submitError,
    handleFeedbackSelect: submitFeedback,
} = useLabInsightActions(props.insightId, "mywork_homepage_carousel", {
    // Callers always pass the server-rendered feedback, so a mount-time fetch would only flash the buttons disabled.
    fetchExistingFeedback: false,
    initialFeedbackValue: () => props.initialFeedbackValue,
});

const feedActions = inject(insightFeedActions, null);

onMounted(() => {
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_CARD_SHOWN, {
        insight_id: props.insightId,
        experiment_slug: props.experimentSlug,
        location: "mywork_homepage_carousel",
        evidence_source_counts: evidenceSourceCounts(props.evidenceGroups ?? []),
    });
});

async function handleFeedbackSelect(value: 0 | 1) {
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_FEEDBACK_SUBMITTED, {
        insight_id: props.insightId,
        experiment_slug: props.experimentSlug,
        location: "mywork_homepage_carousel",
        value,
    });
    if (feedActions?.enabled()) {
        await feedActions.submit({ insight_id: props.insightId, value, location: "mywork_homepage_carousel", comment: null, tags: [] });
        return;
    }
    const response = await submitFeedback(value);
    if (submitError.value) {
        return;
    }
    // The redesigned homepage card is like/dislike-and-done: once feedback is recorded, the
    // carousel removes this insight entirely rather than leaving an already-answered card visible.
    emit("feedback", response);
}
</script>
